import { randomUUID as uuidv4 } from 'expo-crypto';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { useMinimalCartUi } from 'features/cart/hooks/use-minimal-cart-ui';
import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';
import {
  mapChildrenPriceToMenuSelectionsPrice,
  mapMenuSelectionsToICartEntry,
  mapMenuSelectionsToMinimalCartEntry,
  mapSelectedProductToMenuSelections,
} from 'features/cart/utils/minimal-cart';
import { SlotsSelections } from 'features/menu/components/product-wizard/types';
import { useProductWizardFlow } from 'features/menu/components/product-wizard/use-product-wizard-flow';

import { toaster } from 'components/toaster/toaster';
import { useCart } from 'hooks/use-cart';
import { getStaticPathname } from 'navigation/location/location';
import { useAppliedReward } from 'pages/loyalty/loyalty-rewards/use-applied-reward';
import { useFavoritesContext } from 'state/favorites';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { RewardRedemptionMethod } from 'state/loyalty/hooks/types';
import { useLoyaltyOffersMap } from 'state/loyalty/hooks/use-loyalty-offers-map';
import { LoyaltyAppliedOffer } from 'state/loyalty/types';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { mapImageResourceToSanityImage } from 'state/unified-menu/utils';
import { logger } from 'utils/logger';

export const useAddToCart = () => {
  const dispatch = useAppDispatch();
  const { indexedMenu, indexedOffers, indexedRewards } = useUnifiedMenuContext();
  const {
    addItemToMinimalCart,
    updateItemInMinimalCart,
    hydrateMinimalCart,
    removeItemFromMinimalCart,
    cartEntries,
  } = useMinimalCart();
  const { posServiceMode } = useMenuQueryOptions();
  const { cartIdEditing, rewardBenefitId, rewardId, offerId } = useProductWizardFlow();
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const appliedReward = useAppliedReward();
  const { removeFromCart } = useCart();
  const { editFavoriteOneMenuAdapter, tempFavorite: currentFavorite } = useFavoritesContext();
  const loyaltyOffersMap = useLoyaltyOffersMap();

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const selectedLoyaltyOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const enableMinimalCartUi = useMinimalCartUi();

  const getAddToCartSuccessMessage = (
    entry: { name: string; quantity: number },
    isEditing: boolean
  ): string => {
    if (isEditing) {
      return t('updateCartSuccess', { itemName: entry.name });
    }
    if (entry.quantity > 1) {
      return t('addToCartMultipleSuccess', {
        itemName: entry.name,
        quantity: entry.quantity - 1,
      });
    }
    return t('addToCartSuccess', { itemName: entry.name });
  };

  /**
   * @deprecated
   * this function is used to add items to the legacy cart, it supports some of the hydration logic
   * for the legacy cart.
   * We should migrate this function to use the minimal cart.
   * This function will be removed in the future.
   */
  const addToLegacyCart = async (
    selectedProduct: ILocalizedMenuEntry,
    quantity: number,
    slotsSelections: SlotsSelections
  ) => {
    const pathname = getStaticPathname();
    const menuSelections = mapSelectedProductToMenuSelections(
      selectedProduct,
      quantity,
      slotsSelections
    );

    // If there is a favorite active in the context, add to the favorite instead of the cart
    if (currentFavorite) {
      const [mappedEntry] = mapMenuSelectionsToMinimalCartEntry(menuSelections, {
        menu: indexedMenu,
        indexedOffers,
        posServiceMode,
        indexedRewards,
        url: pathname,
        offerId,
      });

      // When editing a favorite, ensure that the cartId is preserved so that upsertEntry can find the existing entry
      const favoriteEntryWithCartId = {
        ...mappedEntry,
        cartId: cartIdEditing || mappedEntry.cartId || uuidv4(),
      };

      return editFavoriteOneMenuAdapter({
        minimalCartEntries: [favoriteEntryWithCartId],
      });
    }

    const rewardBenefitSelection = menuSelections[0];

    const [mappedEntry] = mapMenuSelectionsToMinimalCartEntry(
      rewardId ? [rewardBenefitSelection] : menuSelections,
      {
        menu: indexedMenu,
        indexedOffers,
        indexedRewards,
        posServiceMode,
        url: pathname,
        rewardId,
        offerId,
      }
    );

    const isRewardEntry = !cartIdEditing && rewardBenefitId;

    const minimalCartEntry = {
      ...mappedEntry,
      cartId: cartIdEditing || uuidv4(),
      ...(cartIdEditing && { isEdit: true }),
      ...(isRewardEntry && { rewardBenefitId, rewardId }),
    };

    // Remove already applied reward if applying a new one
    if (enableRewardUnstacking && isRewardEntry && appliedReward) {
      removeFromCart({ cartId: appliedReward.cartId });
    }

    const isHydrationSuccessful = await hydrateMinimalCart([minimalCartEntry]);
    if (!isHydrationSuccessful) {
      return toaster.negative(t('addToCartError', { itemName: minimalCartEntry.name }));
    }

    if (selectedLoyaltyOffer?._id === minimalCartEntry.id) {
      if (!selectedLoyaltyOffer.isStackable) {
        appliedOffers.forEach(offer => {
          if (!offer.isStackable && !!offer.cartId) {
            removeFromCart({ cartId: offer.cartId });
          }
        });
      }
      dispatch(
        actions.loyalty.applyOffer({
          id: selectedLoyaltyOffer.loyaltyEngineId,
          type: (selectedLoyaltyOffer as LoyaltyAppliedOffer).type,
          cartId: minimalCartEntry.cartId,
          isStackable: selectedLoyaltyOffer.isStackable,
          cmsId: selectedLoyaltyOffer._id,
        })
      );
      dispatch(actions.loyalty.setSelectedOffer(null));
    }

    const message = getAddToCartSuccessMessage(minimalCartEntry, !!cartIdEditing);

    return toaster.addToCartToast(
      message,
      rewardId
        ? mapImageResourceToSanityImage(
            selectedProduct.image?.resource ?? '',
            selectedProduct.image?.altText?.locale ?? ''
          )
        : minimalCartEntry.image
    );
  };

  const buildMenuSelections = (
    selectedProduct: ILocalizedMenuEntry,
    quantity: number,
    slotsSelections: SlotsSelections
  ) => {
    let menuSelections = mapSelectedProductToMenuSelections(
      selectedProduct,
      quantity,
      slotsSelections
    );

    if (rewardId) {
      menuSelections = [
        {
          id: rewardId,
          quantity, // Check the stacking logic here?
          menuSelections,
        },
      ];
    }

    return menuSelections;
  };

  const buildMappedEntryWithPrices = (
    menuSelections: ReturnType<typeof buildMenuSelections>,
    url?: string
  ) => {
    const [mappedEntry]: ICartEntry[] = mapMenuSelectionsToICartEntry(menuSelections, {
      menu: indexedMenu,
      posServiceMode,
      url,
      indexedOffers,
      indexedRewards,
      rewardId,
    });

    // The mapped entry has price at children level, we have to add the price to the menu selections tree
    const menuSelectionsWithPrice = mapChildrenPriceToMenuSelectionsPrice(mappedEntry);
    // mappedEntry.menuSelections is readonly, so create a new object with updated menuSelections
    return {
      ...mappedEntry,
      price: {
        amount:
          mappedEntry.type === MinimalCartEntryType.reward
            ? (mappedEntry.price?.amount ?? 0) * -1
            : mappedEntry.price?.amount ?? 0,
        currency: 'USD',
      },
      menuSelections: menuSelectionsWithPrice,
    };
  };

  const maybeAddFavoriteEntry = (mappedEntryWithPrices: ICartEntry): boolean => {
    if (!currentFavorite) {
      return false;
    }

    // When editing a favorite, ensure that the cartId is preserved so that upsertEntry can find the existing entry
    const favoriteEntryWithCartId = {
      ...mappedEntryWithPrices,
      cartId: cartIdEditing || mappedEntryWithPrices.cartId || uuidv4(),
    };
    editFavoriteOneMenuAdapter({
      minimalCartEntries: [favoriteEntryWithCartId],
    });
    return true;
  };

  const buildMinimalCartEntry = (mappedEntryWithPrices: ICartEntry) => ({
    ...mappedEntryWithPrices,
    ...(cartIdEditing && { isEdit: true }),
    cartId: cartIdEditing || uuidv4(),
  });

  const validateIncentiveEntry = <T extends ICartEntry>(minimalCartEntry: T): T => {
    if (minimalCartEntry.type !== MinimalCartEntryType.incentive) {
      return minimalCartEntry;
    }

    const offerEntry = loyaltyOffersMap[minimalCartEntry.id];
    if (!offerEntry) {
      logger.warn({
        message: '[useAddToCart] offerEntry not found',
        context: { minimalCartEntry },
      });
    }
    if (offerEntry && offerEntry.loyaltyEngineId !== minimalCartEntry.referenceId) {
      logger.warn({
        message:
          '[useAddToCart] loyaltyEngineId from oneMenu differs from the one retrieved from loyalty',
        context: {
          loyaltyOfferEntry: offerEntry,
          minimalCartEntry,
        },
      });
      return {
        ...minimalCartEntry,
        referenceId: offerEntry.loyaltyEngineId,
      } as T;
    }

    return minimalCartEntry;
  };

  const removeAppliedRewardIfNeeded = (minimalCartEntry: ICartEntry) => {
    if (
      enableRewardUnstacking &&
      rewardId &&
      !cartIdEditing &&
      minimalCartEntry.type === MinimalCartEntryType.reward &&
      appliedReward
    ) {
      const cartEntryToRemove = cartEntries.find(entry => entry.cartId === appliedReward.cartId);
      if (!cartEntryToRemove) {
        return;
      }
      removeItemFromMinimalCart(cartEntryToRemove);
    }
  };

  const applyRewardIfNeeded = (minimalCartEntry: ICartEntry) => {
    if (rewardId && !cartIdEditing && minimalCartEntry.type === MinimalCartEntryType.reward) {
      // add reward to applied rewards slice in loyalty context and deduct points from user
      dispatch(
        actions.loyalty.applyReward({
          cartId: minimalCartEntry.cartId,
          rewardBenefitId,
          quantity: enableRewardUnstacking ? 1 : minimalCartEntry.quantity,
          redemptionMethod: RewardRedemptionMethod.MOBILE,
          enableRewardUnstacking,
        })
      );
    }
  };

  const persistMinimalCartEntry = (minimalCartEntry: ICartEntry) => {
    if (cartIdEditing) {
      if (minimalCartEntry.quantity > 1) {
        updateItemInMinimalCart(cartIdEditing, { ...minimalCartEntry, quantity: 1 });
        for (let i = 0; i < minimalCartEntry.quantity - 1; i++) {
          const entryCopy = { ...minimalCartEntry, quantity: 1, cartId: uuidv4() };
          addItemToMinimalCart(entryCopy);
        }
      } else {
        updateItemInMinimalCart(cartIdEditing, minimalCartEntry);
      }
    } else {
      if (minimalCartEntry.quantity > 1) {
        if (enableRewardUnstacking && minimalCartEntry.type === MinimalCartEntryType.reward) {
          // the first entry is added as a reward with menuSelections with quantity 1
          addItemToMinimalCart({
            ...minimalCartEntry,
            quantity: 1,
            menuSelections: [
              {
                id: minimalCartEntry.id,
                quantity: 1,
                menuSelections: minimalCartEntry.menuSelections,
              },
            ],
            children: [
              {
                ...minimalCartEntry.children[0],
                quantity: 1,
              },
            ],
          });

          // while the remaining entries are added as items
          for (let i = 0; i < minimalCartEntry.quantity - 1; i++) {
            const childEntry = minimalCartEntry.children?.[0];
            if (!childEntry) {
              continue;
            }
            const childEntryCopy = { ...childEntry, quantity: 1, cartId: uuidv4() };
            addItemToMinimalCart(childEntryCopy);
          }
        } else {
          for (let i = 0; i < minimalCartEntry.quantity; i++) {
            const entryCopy = { ...minimalCartEntry, quantity: 1, cartId: uuidv4() };
            addItemToMinimalCart(entryCopy);
          }
        }
      } else {
        addItemToMinimalCart(minimalCartEntry);
      }
    }
  };

  const showSuccessToast = (minimalCartEntry: ICartEntry, selectedProduct: ILocalizedMenuEntry) => {
    const message = getAddToCartSuccessMessage(minimalCartEntry, !!cartIdEditing);

    return toaster.addToCartToast(
      message,
      rewardId
        ? mapImageResourceToSanityImage(
            selectedProduct.image?.resource ?? '',
            selectedProduct.image?.altText?.locale ?? ''
          )
        : minimalCartEntry.image
    );
  };

  /**
   * This function is used to add items to the minimal cart,
   * it follows one menu logic and add items using a hierarchy
   */
  const addToCart = (
    selectedProduct: ILocalizedMenuEntry,
    quantity: number,
    slotsSelections: SlotsSelections,
    url?: string
  ) => {
    const pathname = getStaticPathname();
    const menuSelections = buildMenuSelections(selectedProduct, quantity, slotsSelections);
    const menuSelectionsWithPrices = buildMappedEntryWithPrices(menuSelections, url ?? pathname);

    // If there is a favorite active in the context, add to the favorite instead of the cart
    if (maybeAddFavoriteEntry(menuSelectionsWithPrices)) {
      return;
    }

    let minimalCartEntry = buildMinimalCartEntry(menuSelectionsWithPrices);
    minimalCartEntry = validateIncentiveEntry(minimalCartEntry);
    removeAppliedRewardIfNeeded(minimalCartEntry);
    applyRewardIfNeeded(minimalCartEntry);
    persistMinimalCartEntry(minimalCartEntry);

    return showSuccessToast(minimalCartEntry, selectedProduct);
  };

  return enableMinimalCartUi ? addToCart : addToLegacyCart;
};
